import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Stack,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material'
import { InputArea, Tag, InputBox, TagVariant } from './components'
import { leastText, primary } from '@/constant'
import CheckIcon from '@mui/icons-material/Check'

export interface TagInputProps {
  size?: number;
  label?: string;
  required?: boolean;
  value: string[];
  onChange: (value: string[]) => void;
  tagVariant?: TagVariant;
  placeholder?: string;
  select?: boolean;
  options?: { value: string | number; label: string; disabled?: boolean }[];
}

const TagInput: React.FC<TagInputProps> = (props) => {
  const {
    value,
    label,
    required,
    onChange,
    size,
    tagVariant,
    placeholder,
    select,
    options,
  } = props
  const [inputPos, setInputPos] = useState<number>(!!value ? value?.length : 0)
  const [currentInput, setInput] = useState<string>('')
  const [autoFocuse, setAutoFocuse] = useState<boolean>(false)
  const inputRef = useRef(null)
  const labelRef = useRef(null)
  const tagsRowRef = useRef(null)

  const [labelWidth, setLabelWidth] = useState<number>(0)
  const [overflow, setOverflow] = useState<boolean>(false)
  const [overflowSize, setOverflowSize] = useState<number>(0)

  const [overflowTagWidth, setOverflowTagWidth] = useState<number>(0)

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event?.target?.value
      setInput(inputValue)
    },
    []
  )

  const onAddTag = useCallback(
    (newTag: string, pos: number) => {
      const newTags = [...(value ?? [])]
      newTags.splice(pos, 0, newTag)
      setInputPos((pos) => pos + 1)
      onChange(newTags)
    },
    [onChange, value]
  )

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent) => {
      const key = event.code
      // if ((key === "Enter" || key === "Semicolon") && currentInput.length > 0) {
      //   if (key === "Semicolon") {
      //     onAddTag(currentInput?.replace(";", ""), inputPos);
      //   } else {
      //     onAddTag(currentInput, inputPos);
      //   }
      //   setInput("");
      // } else
      if (
        (key === 'Left' || key === 'ArrowLeft') &&
        currentInput.length === 0 &&
        inputPos > 0
      ) {
        setInputPos((inputPos) => inputPos - 1)
      } else if (
        (key === 'Right' || key === 'ArrowRight') &&
        currentInput.length === 0 &&
        inputPos < value?.length &&
        value?.length > 0
      ) {
        setInputPos((inputPos) => inputPos + 1)
      }
    },
    [currentInput, inputPos, onAddTag, value?.length]
  )

  const onDeleteTag = useCallback(
    (tagIndex: number) => () => {
      const newTags = [...(value ?? [])]
      newTags.splice(tagIndex, 1)
      onChange(newTags)
      if (inputPos > 0) {
        setInputPos((inputPos) => inputPos - 1)
      }
    },
    [inputPos, onChange, value]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const key = event.code
      if (key === 'Backspace' && currentInput?.length === 0 && inputPos > 0) {
        onDeleteTag(inputPos - 1)()
      }
    },
    [currentInput?.length, inputPos, onDeleteTag]
  )

  const onInputBlure = useCallback(() => {
    if (currentInput.length > 0) {
      onAddTag(currentInput, inputPos)
      setInput('')
    }
    setAutoFocuse(false)
  }, [currentInput, inputPos, onAddTag])

  const onPaste = useCallback(
    (event: React.BaseSyntheticEvent) => {
      event.preventDefault()
      const emailTest = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g
      navigator?.clipboard?.readText()?.then((res) => {
        const contents = res
        if (tagVariant === 'email') {
          const matches = Array.from(contents?.matchAll(emailTest))
          const newTags = [...(value ?? [])]
          matches?.forEach((match) => newTags.push(match?.[0]))
          onChange(newTags)
        } else {
          const newTags = [...(value ?? [])]
          newTags.push(...contents?.split(','))
          onChange(newTags)
        }
      })
    },
    [onChange, tagVariant, value]
  )

  const renderElements = (inputPos: number, currentTags: string[]) => {
    const elements = currentTags?.map((tag, index) => (
      <Tag
        key={`tag-${index}`}
        variant={tagVariant ?? 'text'}
        text={tag}
        onDelete={onDeleteTag(index)}
      />
    ))
    if (size && currentTags?.length >= size) {
      return elements
    } else {
      elements.splice(
        inputPos,
        0,
        <InputBox
          ref={inputRef}
          key={'tag-input'}
          autoFocus={autoFocuse}
          value={currentInput}
          size={currentInput?.length ? currentInput.length + 3 : 1}
          onChange={handleInputChange}
          onKeyUp={handleKeyUp}
          onKeyDown={handleKeyDown}
          onBlur={onInputBlure}
          onPaste={onPaste}
        />
      )
      return elements
    }
  }

  const onClickArea = useCallback(() => {
    if (inputRef && inputRef?.current) {
      // @ts-ignore
      inputRef?.current?.focus()
      setAutoFocuse(true)
    }
  }, [])

  const onSelectFocuse = useCallback(() => {
    setAutoFocuse(true)
  }, [])
  const onSelectBlur = useCallback(() => {
    setAutoFocuse(false)
  }, [])

  const onSelectChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const {
        target: { value },
      } = event
      onChange(typeof value === 'string' ? value.split(',') : value)
    },
    [onChange]
  )

  const renderSelected = useCallback(() => <></>, [])

  useEffect(() => {
    if (labelRef && labelRef?.current) {
      // @ts-ignore
      setLabelWidth(labelRef?.current?.offsetWidth ?? 0)
    }
  }, [labelRef])

  useEffect(() => {
    if (!autoFocuse) {
      setInputPos(!!value ? value?.length : 0)
    }
  }, [value, autoFocuse])

  useEffect(() => {
    if (tagsRowRef?.current) {
      const tagsRowObserver = new ResizeObserver(() => {
        const tagsWidth = document.getElementById(
          `${label}tags-row`
        )?.offsetWidth
        const selectWith = document.getElementById(
          `${label}tags-select`
        )?.offsetWidth
        // @ts-ignore
        if (tagsWidth >= selectWith - 80) {
          setOverflow(true)
        }
      })
      tagsRowObserver.observe(tagsRowRef?.current)
    }
  }, [label, tagsRowRef])

  useEffect(() => {
    const tagsWidth = document.getElementById(`${label}tags-row`)?.offsetWidth
    if (overflow && overflowSize === 0 && overflowTagWidth === 0) {
      setOverflowSize(value?.length - 1)
    }
    if (overflow && overflowSize > 0 && overflowTagWidth === 0) {
      // @ts-ignore
      setOverflowTagWidth(tagsWidth)
    }
    // @ts-ignore
    if (tagsWidth < overflowTagWidth) {
      setOverflow(false)
      setOverflowSize(0)
      setOverflowTagWidth(0)
    }
  }, [overflow, overflowSize, value?.length, overflowTagWidth, label])

  return (
    <Stack sx={{ marginTop: '8px', position: 'relative' }}>
      {label && (
        <InputLabel
          ref={labelRef}
          shrink={value?.length > 0 ? true : autoFocuse}
          required={required}
          sx={{ left: '-14px', top: '-12px' }}
        >
          {label}
        </InputLabel>
      )}
      {select && (
        <Stack
          ref={tagsRowRef}
          id={`${label}tags-row`}
          sx={{
            flexDirection: 'row',
            position: 'absolute',
            zIndex: 1,
            gap: '4px',
            display: 'block',
            whiteSpace: 'nowrap',
            // overflow: "auto",
            // maxWidth: "100%",
          }}
        >
          {overflow
            ? [...value]?.slice(0, overflowSize)?.map((v, index) => (
                <Tag
                  sx={{
                    marginRight: '8px',
                    textDecoration: options?.find((item) => item.value === v)
                      ?.disabled
                      ? 'line-through'
                      : 'none',
                  }}
                  key={`tag-${index}`}
                  variant={tagVariant ?? 'text'}
                  // @ts-ignore
                  text={options?.find((item) => item.value === v)?.label}
                  onDelete={onDeleteTag(index)}
                />
              ))
            : value?.map((v, index) => (
                <Tag
                  sx={{
                    marginRight: '8px',
                    textDecoration: options?.find((item) => item.value === v)
                      ?.disabled
                      ? 'line-through'
                      : 'none',
                  }}
                  key={`tag-${index}`}
                  variant={tagVariant ?? 'text'}
                  // @ts-ignore
                  text={options?.find((item) => item.value === v)?.label}
                  onDelete={onDeleteTag(index)}
                />
              ))}
          {overflow && (
            <Tag
              key={'tag-overflow'}
              variant={'text'}
              text={'...'}
              // @ts-ignore
              onDelete={null}
              noDelete={true}
            />
          )}
        </Stack>
      )}
      {select ? (
        <Select<string[]>
          id={`${label}tags-select`}
          multiple
          onFocus={onSelectFocuse}
          onBlur={onSelectBlur}
          variant={'standard'}
          sx={{
            ...((value?.length === 0 || !value) && {
              '& > div:before': {
                content: `"${placeholder}"`,
                color: leastText,
                fontSize: '14px',
                lineHeight: '22px',
                paddingLeft: autoFocuse ? 'none' : `${labelWidth + 12}px`,
                transition: 'all 0.2s ease-in-out',
              },
            }),
          }}
          value={value}
          onChange={onSelectChange}
          renderValue={renderSelected}
        >
          {options?.map((option) => (
            <MenuItem
              disabled={option?.disabled}
              key={option.value}
              value={option.value}
              sx={{
                display: 'flex',
                textDecoration: option?.disabled ? 'line-through' : 'none',
                ...(value?.includes(option?.value as string) && {
                  color: primary,
                  backgroundColor: 'transparent !important',
                }),
              }}
            >
              {option.label}
              {value?.includes(option?.value as string) && (
                <CheckIcon
                  sx={{ color: primary, fontSize: 16, marginLeft: 'auto' }}
                />
              )}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <InputArea
          focused={autoFocuse ? 'true' : 'false'}
          onClick={onClickArea}
          placeholder={
            value?.length === 0 && currentInput === '' ? placeholder : ''
          }
        >
          {renderElements(inputPos, value ?? [])}
        </InputArea>
      )}
    </Stack>
  )
}

export default TagInput
